import React, { FC, useCallback, useEffect } from 'react';
import withStyles from 'src/common/withStyles';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { decorateComponent } from '../../../../common/helpers';
import OtpForm from '../../form/Otp';
import { ReduxState } from '../../../../types/reduxState';
import { PATHS } from '../../../../common/constants';
import { authRedirect, nextUrl } from '../../../../common/utils/routing';
import { MFAMethod } from '../../../../types/common/MFAMethods';

import s from './Otp.module.scss';

const title = 'Two-factor authentication';
const subtitleMessages = {
  totp: 'Open the two-factor authenticator (TOTP) app on your mobile device to view your authentication code.',
  sms: 'Enter the two-factor authentication code received via TEXT notification',
  push: 'Enter the two-factor authentication code received via PUSH notification',
};

const Otp: FC = () => {
  const { search, hash } = useLocation();
  const { push } = useHistory();
  const currentMFAMethod = useSelector<ReduxState, MFAMethod | null | undefined>(({ user }) => user.get('userMFAMethod', null));
  const currentMFAMethods = useSelector<ReduxState, MFAMethod[] | null | undefined>(({ user }) => user.get('userMFAMethods', null));
  const nextPage = useCallback(
    () => {
      authRedirect(nextUrl(search, hash), push);
    },
    [hash, push, search],
  );
  const onSubmitSuccess = () => nextPage();

  useEffect(() => {
    if (!currentMFAMethod) {
      push(PATHS.LOGIN);
    }
  }, [currentMFAMethod, push]);

  return (
    <div className={s.container}>
      <Helmet>
        <title lang="en">{title}</title>
      </Helmet>
      <h1 className={s.title}>{title}</h1>
      { currentMFAMethod && (
        <>
          <p className={s.message}>
            {subtitleMessages[currentMFAMethod]}
          </p>
          <OtpForm
            currentMFAMethod={currentMFAMethod}
            onSubmitSuccess={onSubmitSuccess}
          />
          <p className={s.message}>
            { currentMFAMethods && currentMFAMethods.length > 1 && (
              <Link to={PATHS.CHOOSE_MFA_METHOD}>Change two-factor authentication method</Link>
            )}
          </p>
          <p className={s.message}>
            Restore using a <Link to={PATHS.MFA_RECOVERY}>recovery code</Link>
          </p>
        </>
      ) }
    </div>
  );
};

const decorators = [
  withStyles(s),
];

export default decorateComponent(Otp, decorators);
